import { HelmetProvider } from "react-helmet-async";
import { Analytics } from "@vercel/analytics/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { PageMeta } from "@/components/PageMeta";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { InstallerOnlyRoute } from "@/components/InstallerOnlyRoute";
import { AuthHashRedirect } from "@/components/AuthHashRedirect";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppLayout } from "@/components/layout/AppLayout";
import Login from "./pages/Login";
import AcceptTerms from "./pages/AcceptTerms";
import AuthCallback from "./pages/AuthCallback";
import AuthConfirm from "./pages/AuthConfirm";
import SetPassword from "./pages/SetPassword";
import Marketing from "./pages/Marketing";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import SolarMonitoringSoftwareForInstallers from "./pages/landing/SolarMonitoringSoftwareForInstallers";
import SolarFleetDashboard from "./pages/landing/SolarFleetDashboard";
import BatteryBackupMonitoring from "./pages/landing/BatteryBackupMonitoring";
import SolarForecastingSoftware from "./pages/landing/SolarForecastingSoftware";
import Dashboard from "./pages/app/Dashboard";
import PropertyDashboard from "./pages/app/PropertyDashboard";
import FleetDashboard from "./pages/app/FleetDashboard";
import Properties from "./pages/app/Properties";
import System from "./pages/app/System";
import History from "./pages/app/History";
import Alerts from "./pages/app/Alerts";
import AutomationFlowBuilder from "./pages/app/AutomationFlowBuilder";
import ControlPanel from "./pages/app/ControlPanel";
import Reports from "./pages/app/Reports";
import Billing from "./pages/app/Billing";
import SettingsRouter from "./pages/app/SettingsRouter";
import PropertySettingsRoute from "./pages/app/PropertySettingsRoute";
import BatteryHealth from "./pages/app/BatteryHealth";
import OnboardingV2 from "./pages/app/OnboardingV2";
import OnboardingWelcome from "./pages/onboarding/OnboardingWelcome";
import WhiteGloveIntake from "./pages/onboarding/WhiteGloveIntake";
import BillingSetup from "./pages/onboarding/BillingSetup";
import WhiteGloveWaiting from "./pages/onboarding/WhiteGloveWaiting";
import WhiteGloveCredentials from "./pages/onboarding/WhiteGloveCredentials";
import NotFound from "./pages/NotFound";
import PendingApproval from "./pages/PendingApproval";
import Invite from "./pages/Invite";
import GatewayDetail from "./pages/app/GatewayDetail";
import Gateways from "./pages/app/Gateways";
import Issues from "./pages/app/Issues";
import Opportunities from "./pages/app/Opportunities";
import Visits from "./pages/app/Visits";
import VisitDetail from "./pages/app/VisitDetail";
import WarrantyDetailPage from "./pages/app/WarrantyDetail";
import FirmwareReleases from "./pages/app/FirmwareReleases";
import AdminDashboard from "./pages/app/AdminDashboard";
import SupportDashboard from "./pages/app/SupportDashboard";
import { AdminOnlyRoute, SuperAdminOnlyRoute } from "@/components/AdminOnlyRoute";
import SparklineHarness from "./pages/dev/SparklineHarness";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60 * 1000 },
  },
});

/** White-glove billing step: systemCount from intake state. */
function WhiteGloveBillingRoute() {
  const location = useLocation();
  const state = location.state as { whiteGloveIntake?: { estimatedSystemCount?: number } } | null;
  const systemCount = state?.whiteGloveIntake?.estimatedSystemCount ?? 0;
  return <BillingSetup whiteGloveMode systemCount={systemCount} />;
}

/** Public page after credentials submitted (no app layout). */
function CredentialsReceivedPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4" data-testid="received-page">
      <p className="text-lg text-muted-foreground">Credentials received — we&apos;ll complete setup within 24 hours.</p>
    </div>
  );
}

/** Redirect /app/automations/:id/edit → /app/automations/:id/flow (flat builder removed). */
function EditToFlowRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/automations/${id}/flow`} replace />;
}

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <AuthProvider>
          <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ErrorBoundary>
              <AuthHashRedirect />
              <PageMeta />
              <Analytics />
              <Routes>
            <Route path="/" element={<Marketing />} />
            <Route path="/marketing" element={<Navigate to="/" replace />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/solar-monitoring-software-for-installers" element={<SolarMonitoringSoftwareForInstallers />} />
            <Route path="/solar-fleet-dashboard" element={<SolarFleetDashboard />} />
            <Route path="/battery-backup-monitoring" element={<BatteryBackupMonitoring />} />
            <Route path="/solar-forecasting-software" element={<SolarForecastingSoftware />} />
            <Route path="/login" element={<Login />} />
            <Route path="/accept-terms" element={<AcceptTerms />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/confirm" element={<AuthConfirm />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/invite/:token" element={<Invite />} />

            {/* Protected App Routes */}
            <Route path="/app" element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="property/:propertyId" element={<PropertyDashboard />} />
              <Route path="property/:propertyId/settings" element={<PropertySettingsRoute />} />
              <Route path="property/:propertyId/gateway" element={<InstallerOnlyRoute><GatewayDetail /></InstallerOnlyRoute>} />
              <Route path="gateways" element={<InstallerOnlyRoute><Gateways /></InstallerOnlyRoute>} />
              <Route path="fleet" element={<InstallerOnlyRoute><FleetDashboard /></InstallerOnlyRoute>} />
              <Route path="properties" element={<Properties />} />
              <Route path="system" element={<System />} />
              <Route path="battery-health/:plantId" element={<BatteryHealth />} />
              <Route path="history" element={<Navigate to="/app/analytics" replace />} />
              <Route path="analytics" element={<History />} />
              <Route path="notifications" element={<Navigate to="/app/automations" replace />} />
              <Route path="alerts" element={<Navigate to="/app/automations" replace />} />
              <Route path="automations" element={<InstallerOnlyRoute><Alerts /></InstallerOnlyRoute>} />
              <Route path="automations/new" element={<InstallerOnlyRoute><AutomationFlowBuilder /></InstallerOnlyRoute>} />
              <Route path="automations/new/flow" element={<InstallerOnlyRoute><AutomationFlowBuilder /></InstallerOnlyRoute>} />
              <Route path="automations/:id/edit" element={<EditToFlowRedirect />} />
              <Route path="automations/:id/flow" element={<InstallerOnlyRoute><AutomationFlowBuilder /></InstallerOnlyRoute>} />
              <Route path="opportunities" element={<InstallerOnlyRoute><Opportunities /></InstallerOnlyRoute>} />
              <Route path="issues" element={<InstallerOnlyRoute><Issues /></InstallerOnlyRoute>} />
              <Route path="visits" element={<InstallerOnlyRoute><Visits /></InstallerOnlyRoute>} />
              <Route path="visits/:visitId" element={<InstallerOnlyRoute><VisitDetail /></InstallerOnlyRoute>} />
              <Route path="properties/:propertyId/warranties/:warrantyId" element={<InstallerOnlyRoute><WarrantyDetailPage /></InstallerOnlyRoute>} />
              <Route path="control" element={<InstallerOnlyRoute><ControlPanel /></InstallerOnlyRoute>} />
              <Route path="reports" element={<InstallerOnlyRoute><Reports /></InstallerOnlyRoute>} />
              <Route path="billing" element={<Billing />} />
              <Route path="settings" element={<SettingsRouter />} />
              <Route path="admin" element={<AdminOnlyRoute><AdminDashboard /></AdminOnlyRoute>} />
              <Route path="admin/support" element={<SuperAdminOnlyRoute><SupportDashboard /></SuperAdminOnlyRoute>} />
              <Route path="admin/firmware" element={<AdminOnlyRoute><FirmwareReleases /></AdminOnlyRoute>} />
              <Route path="onboarding" element={<OnboardingV2 />} />
              <Route path="onboarding/welcome" element={<OnboardingWelcome />} />
              <Route path="onboarding/white-glove/intake" element={<WhiteGloveIntake />} />
              <Route path="onboarding/white-glove/billing" element={<WhiteGloveBillingRoute />} />
              <Route path="onboarding/white-glove/waiting" element={<WhiteGloveWaiting />} />
            </Route>

            {/* Dev harness routes (no auth) */}
            <Route path="/dev/sparkline-harness" element={<SparklineHarness />} />

            {/* Public: credential portal (email link; no app layout, no auth) */}
            <Route path="onboarding/credentials/:token" element={<WhiteGloveCredentials />} />
            <Route path="onboarding/credentials/received" element={<CredentialsReceivedPage />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
